<template>
	<div class="bg5 p2">
		<div class="xs-12 flex wrap xs-between">
			<div class="fw7 fs4 c6 lh125">{{ productName }}</div>
		</div>
		<!-- header -->
		<div class="xs-12 mt6 flex">
			<div class="xs-6 fs3 fw7">{{ t("key") }}</div>
			<div class="xs-2 fs3 fw7">{{ t("rate") }} %</div>
			<div class="xs-2 fs3 fw7">{{ t("value") }}</div>
			<div class="xs-2 fs3 fw7">{{ t("premium") }}</div>
		</div>
		<!-- list -->
		<div class="mt2 mb6">
			<div
				v-for="(value, name) in values"
				:key="name"
				class="flex xs-12 pb1 xs-middle"
			>
				<div class="xs-6 fs3">{{ value.name }}</div>

				<!-- rate -->
				<div class="xs-2 pr1">
					<core-form-input
						v-if="isEditable"
						componentName="j-base-input-number"
						:name="`${name}-value`"
						type="float"
						size="small"
						v-model="value.rate"
						v-validate="'required'"
						:validationErrors="errors"
						aria-autocomplete="none"
						@input="debouncedFetchPrice"
					/>
					<div v-if="!isEditable" class="fs3">{{ value.rate }}</div>
				</div>
				<!-- value -->
				<div class="xs-2">
					<core-form-input
						v-if="isEditable"
						componentName="j-base-input-number"
						:name="`${name}-rate`"
						type="float"
						size="small"
						v-validate="'required'"
						v-model="value.value"
						:validationErrors="errors"
						aria-autocomplete="none"
						@input="debouncedFetchPrice"
					/>
					<div v-if="!isEditable" class="fs3">{{ value.value }}</div>
				</div>
				<div class="xs-2 flex xs-middle xs-center fs3">
					{{ parseFloat(((value.value * value.rate) / 100).toFixed(6)) }}
				</div>
			</div>
		</div>

		<div
			class="mb3 flex xs-around xs-middle"
			v-for="(item, i) in appliedTaxes"
			:key="i"
		>
			<div class="xs-6">
				<span class="fw7">{{ item.name }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fw7">{{ item.value }}</span>
			</div>
		</div>
		<div class="flex xs-around xs-middle">
			<div class="xs-6">
				<span class="fw7">{{ t("total_amount") }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fw7">{{ computedPrice }}</span>
			</div>
		</div>
		<!-- Save value -->
		<j-button class="mt6" size="small" @click="saveValues" v-if="isEditable">{{
			t("save_values")
		}}</j-button>
	</div>
</template>

<script>
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import debounce from "lodash.debounce"

import JButton from "@/components/JButton"
import CoreFormInput from "@/modules/core/components/CoreFormInput"

export default {
	props: {
		productId: {
			validator(value) {
				return typeof value === "object"
			}
		},
		rawValues: Array,
		policyId: String,
		isEditable: {
			type: Boolean,
			default: false
		},
		contractInsured: Array,
		endorsementId: {
			type: String,
			required: false,
			default: undefined
		}
	},

	components: {
		CoreFormInput,
		JButton
	},

	data() {
		return {
			productName: "",
			price: null,
			values: this.mergeValuesWithInsureds(
				this.rawValues ? this.massageValuesFromAPI(this.rawValues) : {}
			),
			label: {
				property: "Maximum Property Value"
			},
			isDisabled: null,
			appliedTaxes: null
		}
	},

	computed: {
		computedValues() {
			return this.values
		},
		computedPrice() {
			return this.price
		},
		computedTaxes() {
			return this.appliedTaxes
		},
		plan() {
			return this.productId?.plan
		},
		debug() {
			return this.mergeValuesWithInsureds(
				this.rawValues ? this.massageValuesFromAPI(this.rawValues) : {}
			)
		}
	},

	methods: {
		t(key) {
			const locale = {
				"en-US": {
					key: "Key",
					rate: "Rate",
					save_values: "Save Values",
					premium: "Premium",
					total_amount: "Total Amount",
					retail: "Retail",
					wholesale: "Wholesale",
					value: "Value"
				},
				"zh-HK": {}
			}
			return locale[this.$store.state.settings.locale][key]
		},

		async fetchPrice() {
			const isValid = await this.$validator.validate()
			if (!isValid) return

			const query = gql`
				query getPrice($where: productWhereInput, $values: [keyValueInput]) {
					products: products_2(where: $where, values: $values) {
						list {
							name
							productId {
								plan
							}
							pricing(values: $values) {
								appliedTaxes {
									name
									code
									ratio
									formattedFlat
								}
								formattedPrice
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					where: {
						productId: {
							plan: this.plan
						}
					},
					...this.massageValuesForAPI()
				}
			})

			this.productName = res?.data?.products?.list?.[0]?.name
			this.price = res?.data?.products?.list?.[0]?.pricing?.formattedPrice
			this.appliedTaxes = res?.data?.products?.list?.[0]?.pricing?.appliedTaxes.map(
				item => {
					return {
						name: item.name ? item.name : item.code,
						value: this.getValue(item).flat
					}
				}
			)
		},

		// Add rows in insureds for values and remove rows if it is not related to any insured
		mergeValuesWithInsureds(values) {
			// Check if all insureds are in values, add them with empty rate and value if not
			const valuesIds = Object.values(values).map(value => value.id)
			this.contractInsured.forEach(insured => {
				if (!valuesIds.includes(insured.id)) {
					values = {
						...values,
						[Object.keys(values)?.length]: {
							...insured,
							name: insured.name,
							value: 0,
							rate: 0
						}
					}
				}
			})
			// Check if an insured has been removed, and remove the corresponding value
			const insuredsIds = Object.values(this.contractInsured).map(
				insured => insured.id
			)
			Object.keys(values).forEach(key => {
				if (!insuredsIds.includes(values[key]?.id)) delete values[key]
			})
			return values
		},

		debouncedFetchPrice: debounce(function() {
			this.fetchPrice()
		}, 500),

		async saveValues() {
			// Validate
			const isValid = await this.$validator.validate()
			if (!isValid) {
				this.$store.dispatch("showError", [
					"There are some missing or incorrect fields in the form"
				])
				return
			}

			const query = gql`
				mutation updatePolicy2(
					$input: updatePolicyInput!
					$endorsementId: String
					$policyId: String!
				) {
					updatePolicy2(
						policyId: $policyId
						input: $input
						endorsementId: $endorsementId
					) {
						errors
						status
					}
				}
			`
			const variables = {
				policyId: this.policyId,
				endorsementId: this.endorsementId,
				input: this.massageValuesForAPI()
			}

			const res = await api({
				query,
				variables
			})

			if (!res?.data?.updatePolicy2?.errors)
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.t("values_saved")
					}
				})
			this.$emit("pricing:updated")
		},

		massageValuesFromAPI(values) {
			const newValues = {}
			values
				?.find(item => item.key === "insureds")
				?.value?.arrayValue.forEach((item, i) => {
					let key = item?.objectValue[0]?.key // tour
					let value = item?.objectValue[0]?.value?.objectValue // array for 1 insured
					newValues[i] = {
						rate: parseFloat(
							(
								value.find(v => v.key === "rate")?.value?.numberValue * 100
							).toFixed(6)
						),
						name: value.find(v => v.key === "name")?.value?.stringValue,
						id: value.find(v => v.key === "id")?.value?.stringValue,
						value: value.find(v => v.key === "value")?.value?.numberValue
					}
				})
			return newValues
		},

		massageValuesForAPI() {
			return {
				values: [
					{
						key: "insureds",
						value: {
							arrayValue: Object.values(this.computedValues).map(
								computedValue => {
									return {
										objectValue: [
											{
												key: "tour",
												value: {
													objectValue: [
														{
															key: "rate",
															value: {
																numberValue: computedValue.rate / 100
															}
														},
														{
															key: "value",
															value: {
																numberValue: computedValue.value
															}
														},
														{
															key: "name",
															value: {
																stringValue: computedValue.name
															}
														},
														{
															key: "id",
															value: {
																stringValue: computedValue.id
															}
														}
													]
												}
											}
										]
									}
								}
							)
						}
					}
				]
			}
		},

		getValue(item) {
			if (!item) return ""
			const ratio = item.ratio ? item.ratio * 100 : null
			const flat = item.formattedFlat ? item.formattedFlat : "0"
			return {
				flat,
				summary: ratio
					? `(${ratio ? ratio.toFixed(2) : "0"}% of ${
							item.formattedOriginalPrice
					  })`
					: ""
			}
		}
	},

	async created() {
		await this.fetchPrice()
	},

	watch: {
		contractInsured: {
			handler() {
				this.values = this.mergeValuesWithInsureds(
					this.rawValues ? this.massageValuesFromAPI(this.rawValues) : {}
				)
			}
		}
	}
}
</script>
